import { AnchorItem } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/types';
import { useCallback, useRef } from 'react';
import {
  CHILD_ITEM_OFFSET,
  SCROLL_IDLE_THRESHOLD_MS,
  SCROLL_OFFSET,
  SCROLL_POSITION_TOLERANCE,
} from './constants';
import { isChildItem } from './utils';

export const useAnchorClick = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isClickingRef: React.MutableRefObject<boolean>,
  setCurrentAnchor: (anchor: string) => void,
  anchorItems: AnchorItem[],
  targetScrollRef?: React.MutableRefObject<number | null>,
  lastAnchorRef?: React.MutableRefObject<string | null>
) => {
  const scrollEndCleanupRef = useRef<(() => void) | null>(null);
  // Track click ID to ignore stale callbacks from previous clicks
  const clickIdRef = useRef<number>(0);

  // Clean up any pending scroll detection
  const cleanupScrollDetection = useCallback(() => {
    if (scrollEndCleanupRef.current) {
      scrollEndCleanupRef.current();
      scrollEndCleanupRef.current = null;
    }
  }, []);

  // Detect when scroll completes using scrollend event + idle detection fallback
  const setupScrollEndDetection = useCallback(
    (drawerBody: HTMLElement, targetScrollTop: number, clickId: number) => {
      let idleTimeoutId: NodeJS.Timeout | null = null;
      let rafId: number | null = null;
      let lastScrollTop = drawerBody.scrollTop;
      let isCleanedUp = false;

      const finishScrolling = () => {
        if (isCleanedUp) return;

        isCleanedUp = true;

        // Remove event listeners immediately
        drawerBody.removeEventListener('scrollend', handleScrollEnd);
        if (idleTimeoutId) clearTimeout(idleTimeoutId);
        if (rafId) cancelAnimationFrame(rafId);

        // SAFARI FIX: Delay setting isClickingRef to false until after any
        // pending scroll events are processed. Safari queues scroll events
        // that fire AFTER scrollend, which would incorrectly update the anchor.
        // Using double RAF ensures we skip past Safari's queued scroll events.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Check if this callback is from the current click or a stale one
            if (clickIdRef.current !== clickId) {
              return;
            }
            isClickingRef.current = false;
            if (targetScrollRef) {
              targetScrollRef.current = null;
            }
          });
        });
      };

      // Modern browsers: use scrollend event
      const handleScrollEnd = () => {
        finishScrolling();
      };

      // Fallback: detect scroll idle (no movement for SCROLL_IDLE_THRESHOLD_MS)
      const checkScrollIdle = () => {
        if (isCleanedUp) return;

        const currentScrollTop = drawerBody.scrollTop;

        // Check if we've reached target (within tolerance)
        const reachedTarget =
          Math.abs(currentScrollTop - targetScrollTop) <=
          SCROLL_POSITION_TOLERANCE;

        // Check if scroll has stopped moving
        const hasStoppedMoving = currentScrollTop === lastScrollTop;

        if (reachedTarget || hasStoppedMoving) {
          // Wait a bit more to confirm scroll is truly stopped
          if (idleTimeoutId) clearTimeout(idleTimeoutId);
          idleTimeoutId = setTimeout(() => {
            // Double-check scroll is still at same position
            if (drawerBody.scrollTop === currentScrollTop) {
              finishScrolling();
            } else {
              // Still scrolling, continue checking
              lastScrollTop = drawerBody.scrollTop;
              rafId = requestAnimationFrame(checkScrollIdle);
            }
          }, SCROLL_IDLE_THRESHOLD_MS);
        } else {
          lastScrollTop = currentScrollTop;
          rafId = requestAnimationFrame(checkScrollIdle);
        }
      };

      // Try to use scrollend event (Chrome 114+, Firefox 109+, Safari 17+)
      drawerBody.addEventListener('scrollend', handleScrollEnd, { once: true });

      // Also start idle detection as fallback
      rafId = requestAnimationFrame(checkScrollIdle);

      // Return cleanup function
      return () => {
        finishScrolling();
      };
    },
    [isClickingRef, targetScrollRef]
  );

  // Core scroll logic that can be used by both click handler and programmatic scroll
  const scrollToHref = useCallback(
    (href: string) => {
      const targetElement = contentWrapperRef.current?.querySelector(href);
      if (!(targetElement instanceof HTMLElement)) {
        return;
      }

      // Clean up any previous scroll detection
      cleanupScrollDetection();

      // Increment click ID to invalidate any pending callbacks from previous clicks
      clickIdRef.current += 1;
      const thisClickId = clickIdRef.current;

      isClickingRef.current = true;
      setCurrentAnchor(href);
      // Sync the last anchor cache to prevent stale state after click if lastAnchorRef is provided
      if (lastAnchorRef) {
        lastAnchorRef.current = href;
      }

      const drawerBody = contentWrapperRef.current?.closest('.ant-drawer-body');
      if (!(drawerBody instanceof HTMLElement)) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (targetScrollRef) {
          targetScrollRef.current = null;
        }
        // For scrollIntoView, we can't easily detect end, use short timeout
        setTimeout(() => {
          isClickingRef.current = false;
        }, 500);
        return;
      }

      // Use offsetTop for absolute position (not affected by current scroll animation)
      const containerStyles = window.getComputedStyle(drawerBody);
      const containerPaddingTop = parseFloat(containerStyles.paddingTop) || 0;
      const childOffset = isChildItem(anchorItems, href)
        ? CHILD_ITEM_OFFSET
        : 0;

      // Calculate target scroll position from element's absolute offset
      const targetScrollTop =
        targetElement.offsetTop -
        containerPaddingTop -
        SCROLL_OFFSET +
        childOffset;
      const maxScrollTop = drawerBody.scrollHeight - drawerBody.clientHeight;
      const finalScrollTop = Math.min(
        Math.max(0, targetScrollTop),
        maxScrollTop
      );

      // Store target scroll position for manual scroll detection
      if (targetScrollRef) {
        targetScrollRef.current = finalScrollTop;
      }

      // Set up scroll completion detection
      scrollEndCleanupRef.current = setupScrollEndDetection(
        drawerBody,
        finalScrollTop,
        thisClickId
      );

      // Start smooth scroll
      drawerBody.scrollTo({
        top: finalScrollTop,
        behavior: 'smooth',
      });
    },
    [
      anchorItems,
      cleanupScrollDetection,
      contentWrapperRef,
      isClickingRef,
      lastAnchorRef,
      setCurrentAnchor,
      setupScrollEndDetection,
      targetScrollRef,
    ]
  );

  // Handle anchor click - scroll to target
  const handleAnchorClick = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      link: { title: React.ReactNode; href: string }
    ) => {
      e.preventDefault();
      scrollToHref(link.href);
    },
    [scrollToHref]
  );

  return {
    handleAnchorClick,
    cleanupScrollDetection,
    scrollToHref,
  };
};
